import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';

const findVisiblePanelForField = (fieldRoot) => {
  const dropdown = fieldRoot?.querySelector('.p-dropdown');
  if (!dropdown?.classList.contains('p-inputwrapper-focus')) {
    return null;
  }

  const panels = document.querySelectorAll('.p-dropdown-panel');
  for (let i = panels.length - 1; i >= 0; i -= 1) {
    const panel = panels[i];
    const { width, height } = panel.getBoundingClientRect();
    if (width > 0 && height > 0) {
      return panel;
    }
  }

  return null;
};

const selectFirstVisibleDropdownItem = (panel) => {
  const items = panel.querySelectorAll(
    'li.p-dropdown-item:not(.p-disabled):not(.p-dropdown-item-empty)'
  );

  if (!items.length) {
    return false;
  }

  items[0].click();
  return true;
};

export const TitledDropdown = forwardRef((props, ref) => {
  const {
    id,
    title = '',
    value,
    options = [],
    optionLabel = 'label',
    optionValue = 'value',
    onChange,
    showFilter = false,
    filterBy = null,
    filterMatchMode = 'contains',
    filterLocale = undefined,
    showClear = false,
    className = '',
    // readOnly = false,
    disabled = false,
    placeholder = '',
    error = null,
    required = false,
    optionTemplate = null,
    optionDisabled,
    selectFirstOnFilterEnter = false,
    onShow,
    onHide,
  } = props;

  const fieldRef = useRef(null);
  const filterKeydownRef = useRef(null);
  const attachRetryTimerRef = useRef(null);

  const detachFilterEnterListener = useCallback(() => {
    if (attachRetryTimerRef.current) {
      window.clearTimeout(attachRetryTimerRef.current);
      attachRetryTimerRef.current = null;
    }

    if (!filterKeydownRef.current) {
      return;
    }

    const { input, handler } = filterKeydownRef.current;
    input.removeEventListener('keydown', handler, true);
    filterKeydownRef.current = null;
  }, []);

  const attachFilterEnterListener = useCallback(() => {
    if (!selectFirstOnFilterEnter || !showFilter) {
      return false;
    }

    detachFilterEnterListener();

    const panel = findVisiblePanelForField(fieldRef.current);
    const filterInput = panel?.querySelector('.p-dropdown-filter');
    if (!filterInput) {
      return false;
    }

    const handler = (event) => {
      if (event.key !== 'Enter' && event.which !== 13) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      const activePanel = findVisiblePanelForField(fieldRef.current);
      if (activePanel && selectFirstVisibleDropdownItem(activePanel)) {
        detachFilterEnterListener();
      }
    };

    filterInput.addEventListener('keydown', handler, true);
    filterKeydownRef.current = { input: filterInput, handler };
    return true;
  }, [selectFirstOnFilterEnter, showFilter, detachFilterEnterListener]);

  const scheduleAttachFilterEnterListener = useCallback(
    (retry = 0) => {
      if (attachFilterEnterListener()) {
        return;
      }

      if (retry >= 10) {
        return;
      }

      attachRetryTimerRef.current = window.setTimeout(
        () => scheduleAttachFilterEnterListener(retry + 1),
        50
      );
    },
    [attachFilterEnterListener]
  );

  useEffect(() => () => detachFilterEnterListener(), [detachFilterEnterListener]);

  const handleShow = (event) => {
    onShow?.(event);
    if (selectFirstOnFilterEnter && showFilter) {
      scheduleAttachFilterEnterListener();
    }
  };

  const handleHide = (event) => {
    detachFilterEnterListener();
    onHide?.(event);
  };

  return (
    <div className="field m-0" ref={fieldRef}>
      <label htmlFor={`txt_${id}`}>
        {required && <i className="text-red-400 pi pi-check mr-1" />}
        {title}
      </label>
      <Dropdown
        inputRef={ref}
        className={classNames(`w-full ${className}`, {
          'p-invalid': error?.message,
        })}
        value={value}
        onChange={(e) => onChange(e.value)}
        options={options}
        optionLabel={optionLabel}
        optionValue={optionValue}
        filter={showFilter}
        filterBy={filterBy || 'label'}
        filterMatchMode={filterMatchMode}
        filterLocale={filterLocale}
        showClear={showClear}
        disabled={disabled}
        placeholder={placeholder}
        itemTemplate={optionTemplate || null}
        resetFilterOnHide
        emptyMessage="선택 가능한 옵션 없음"
        optionDisabled={optionDisabled || false}
        onShow={handleShow}
        onHide={handleHide}
      />
      <small id={`txt_err_${id}`} className="p-error">
        {error?.message}
      </small>
    </div>
  );
});
